import { RcFile } from 'antd/lib/upload';
import {
  createContext, ReactNode, useContext, useMemo, useState
} from 'react';
import { AIChatDraftResponse } from '../../services/AIService/types';

export interface MessageStorageContextProviderProps {
  children: ReactNode;
}

export interface DraftValue {
  message?: string;
  file?: RcFile;
  aiResponse?: AIChatDraftResponse;
}

export interface MessageStorageContextValue {
  getDraftById: (patientId: string) => DraftValue;
  setDraftMessage: (patientId: string, message: string) => void;
  setDraftFile: (patientId: string, file?: RcFile) => void;
  reset: () => void;
  setAIDraftResponse: (patientId: string, response: AIChatDraftResponse) => void;
}

export const MessageStorageContext = createContext<MessageStorageContextValue | undefined>(
  undefined
);

export const useMessageStorageContext = () => {
  const context = useContext(MessageStorageContext);
  return (context || {}) as MessageStorageContextValue;
};

export const MessageStorageContextProvider = ({
  children
}: MessageStorageContextProviderProps) => {
  const [messageMap, setMessageMap] = useState<Record<string, string>>({});
  const [fileMap, setFileMap] = useState<Record<string, RcFile | undefined>>({});
  const [aiMessageMap, setAIMessageMap] = useState<
        Record<string, AIChatDraftResponse | undefined>>({});

  const getDraftById = (patientId: string) => ({
    message: messageMap[patientId],
    file: fileMap[patientId],
    aiResponse: aiMessageMap[patientId],
  });

  const setDraftMessage = (patientId: string, message: string) => {
    setMessageMap({
      ...messageMap,
      [patientId]: message
    });
  };

  const setDraftFile = (patientId: string, file?: RcFile) => {
    setFileMap({
      ...fileMap,
      [patientId]: file
    });
  };

  const setAIDraftResponse = (patientId: string, response: AIChatDraftResponse) => {
    setAIMessageMap({
      ...aiMessageMap,
      [patientId]: response
    });
  };

  const reset = () => {
    setMessageMap({});
    setFileMap({});
  };

  const value = useMemo(() => ({
    getDraftById,
    setDraftMessage,
    setDraftFile,
    reset,
    setAIDraftResponse
  }), [getDraftById, setDraftMessage]);

  return (
    <MessageStorageContext.Provider
      value={value}
    >
      {children}
    </MessageStorageContext.Provider>
  );
};
